import React, { useCallback, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useModalize } from 'react-native-modalize'
import { Pressable, View } from 'react-native'

import { isValidPassword } from '@ambire-common/services/validations'
import FaceIDIcon from '@common/assets/svg/FaceIDIcon'
import FingerprintIcon from '@common/assets/svg/FingerprintIcon'
import BottomSheet from '@common/components/BottomSheet'
import Button from '@common/components/Button'
import InputPassword from '@common/components/InputPassword'
import { isWeb } from '@common/config/env'
import { isMobileWebApp } from '@common/config/env/env'
import { useTranslation } from '@common/config/localization'
import { DEVICE_SUPPORTED_AUTH_TYPES } from '@common/contexts/biometricsContext/constants'
import useBiometrics from '@common/hooks/useBiometrics'
import { FONT_FAMILIES } from '@common/hooks/useFonts'
import useController from '@common/hooks/useController'
import useTheme from '@common/hooks/useTheme'
import UnlockPanel from '@common/modules/keystore/components/UnlockPanel'
import useKeyStoreUnlock from '@common/modules/keystore/hooks/useKeyStoreUnlock'
import alert from '@common/services/alert'
import spacings from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import flexbox from '@common/styles/utils/flexbox'
import {
  MobileLayoutContainer,
  MobileLayoutWrapperMainContent
} from '@mobile/components/MobileLayoutWrapper'
import { FEATURES } from '@web/constants/features'

import { resetMobileWebApp } from '../../../../../mobile-web/services/appReset/appReset'
import {
  getPasskeyPrf,
  hasRegisteredPasskey,
  isPasskeyPrfSupported,
  loadPasskeyHandle,
  PASSKEY_SECRET_ID
} from '../../../../../mobile-web/services/passkey/passkey'
import ForgotPasswordModal from './ForgotPasswordModal'

const KeyStoreUnlockScreen = () => {
  const { control, handleSubmit, errors, passwordFieldError, disableSubmit, handleUnlock } =
    useKeyStoreUnlock()
  const { t } = useTranslation()

  const { hasKeystoreRecovery } = useController('EmailVaultController').state
  const {
    state: { statuses, errorMessage, hasBiometricsSecret },
    dispatch: keystoreDispatch
  } = useController('KeystoreController')
  const { theme, themeType } = useTheme()

  const { isLoading, getBiometricsSecret, deviceSupportedAuthTypes } = useBiometrics()
  const hasFaceId = deviceSupportedAuthTypes.includes(
    DEVICE_SUPPORTED_AUTH_TYPES.FACIAL_RECOGNITION
  )
  const BiometricsIcon = hasFaceId ? FaceIDIcon : FingerprintIcon

  const [unlockMethod, setUnlockMethod] = useState<'biometrics' | 'password' | null>(null)
  const [initialCheckDone, setInitialCheckDone] = useState(false)

  // "Forgot password" full-reset recovery (mobile-web only). Wipes the keystore +
  // accounts + seeds and reloads into fresh onboarding to re-import via the seed.
  const { ref: forgotRef, open: openForgot, close: closeForgot } = useModalize()
  const handleConfirmReset = useCallback(() => {
    resetMobileWebApp().catch(() => {})
  }, [])

  // Phase 5 (mobile-web only): show "Unlock with passkey" when the flag is on, the
  // browser/context supports WebAuthn PRF, and a passkey was registered for this
  // wallet. The password path always remains as the recovery fallback.
  const [canPasskey] = useState(
    () => FEATURES.passkeyUnlockEnabled && isPasskeyPrfSupported() && hasRegisteredPasskey()
  )

  const handlePasskeyPrompt = useCallback(async () => {
    try {
      const handle = loadPasskeyHandle()
      if (!handle) return
      const prfKey = await getPasskeyPrf(handle)
      keystoreDispatch({
        type: 'method',
        params: {
          method: 'unlockWithSecret',
          args: [PASSKEY_SECRET_ID, prfKey]
        }
      })
    } catch (e) {
      console.log('Passkey: unlock failed or cancelled', e)
    }
  }, [keystoreDispatch])

  const handleBiometricsPrompt = useCallback(async () => {
    try {
      const biometricsSecret = await getBiometricsSecret()
      if (biometricsSecret) {
        keystoreDispatch({
          type: 'method',
          params: {
            method: 'unlockWithSecret',
            args: ['biometrics', biometricsSecret]
          }
        })
      }
    } catch (e) {
      console.log('Biometrics: Authentication failed or cancelled', e)
      // User cancelled or authentication failed (SecureStore throws/rejects on failure with requireAuthentication)
      // We don't need to do much here, the OS already showed the error/prompt.
    }
  }, [getBiometricsSecret, keystoreDispatch])

  useEffect(() => {
    if (unlockMethod) return

    if (hasBiometricsSecret) {
      setUnlockMethod('biometrics')
    } else {
      setUnlockMethod('password')
    }
  }, [hasBiometricsSecret])

  useEffect(() => {
    if (!isLoading && !initialCheckDone) {
      setInitialCheckDone(true)
      if (hasBiometricsSecret) {
        setUnlockMethod('biometrics')
        handleBiometricsPrompt().catch(() => {})
      }
    }
  }, [
    isLoading,
    hasBiometricsSecret,
    initialCheckDone,
    handleBiometricsPrompt,
    getBiometricsSecret
  ])

  return (
    <MobileLayoutContainer backgroundColor={theme.onboardingPageBackground}>
      <MobileLayoutWrapperMainContent
        withScroll
        contentContainerStyle={[flexbox.flex1, flexbox.justifyCenter]}
      >
        <UnlockPanel>
          {unlockMethod === 'password' && (
            <>
              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <InputPassword
                    testID="passphrase-field"
                    onBlur={onBlur}
                    placeholder={t('Enter your password')}
                    autoFocus={isWeb}
                    inputStyle={{ height: 46 }} // 48-2px border
                    borderWrapperStyle={{ borderRadius: 8 }}
                    inputWrapperStyle={{
                      height: 48,
                      borderRadius: 8 - 2
                    }}
                    onChangeText={(val: string) => {
                      onChange(val)
                      if (errorMessage) {
                        keystoreDispatch({
                          type: 'method',
                          params: {
                            method: 'resetErrorState',
                            args: []
                          }
                        })
                      }
                    }}
                    isValid={!errors.password && isValidPassword(value)}
                    value={value}
                    onSubmitEditing={handleSubmit((data) => handleUnlock(data))}
                    error={passwordFieldError}
                    containerStyle={{ ...spacings.mbSm, width: '100%' }}
                  />
                )}
                name="password"
              />
              {FEATURES.forgotPasswordResetEnabled && isMobileWebApp && (
                <View style={{ alignSelf: 'flex-end', ...spacings.mbSm }}>
                  <Button
                    testID="button-forgot-password"
                    text={t('Forgot password?')}
                    type="ghost2"
                    hasBottomSpacing={false}
                    onPress={() => openForgot()}
                    textStyle={{
                      color: theme.primaryText,
                      fontFamily: FONT_FAMILIES.REGULAR,
                      textDecorationLine: 'underline'
                    }}
                  />
                </View>
              )}
              <Button
                testID="button-unlock"
                type="primary"
                size="large"
                style={{ width: '100%', height: 48, borderRadius: 8 }}
                disabled={disableSubmit}
                hasBottomSpacing={false}
                text={statuses.unlockWithSecret === 'LOADING' ? t('Unlocking...') : t('Unlock')}
                onPress={handleSubmit((data) => handleUnlock(data))}
              />
              {!!hasKeystoreRecovery && (
                <Button
                  text={t('Forgot password?')}
                  type="ghost2"
                  hasBottomSpacing={false}
                  onPress={() => alert('Coming soon!')}
                  style={spacings.mtSm}
                  textStyle={{ textDecorationLine: 'underline' }}
                />
              )}
              {hasBiometricsSecret && (
                <Button
                  text={hasFaceId ? t('Unlock with Face ID') : t('Unlock with fingerprint')}
                  type="secondary"
                  hasBottomSpacing={false}
                  onPress={() => {
                    setUnlockMethod('biometrics')
                    handleBiometricsPrompt().catch(() => {})
                  }}
                  style={spacings.mtSm}
                  childrenPosition="left"
                >
                  <BiometricsIcon
                    width={24}
                    height={24}
                    color={theme.primaryText}
                    style={spacings.mrSm}
                  />
                </Button>
              )}
              {canPasskey && (
                <Button
                  testID="button-unlock-passkey"
                  type="outline"
                  size="large"
                  text={t('Unlock with passkey')}
                  disabled={disableSubmit}
                  style={{
                    width: '100%',
                    height: 48,
                    borderRadius: 8,
                    backgroundColor: theme.onboardingImportButtonBackground,
                    borderColor: theme.primary,
                    ...spacings.mtSm
                  }}
                  textStyle={{ color: theme.primaryText }}
                  hasBottomSpacing={false}
                  onPress={() => {
                    handlePasskeyPrompt().catch(() => {})
                  }}
                />
              )}
            </>
          )}
          {unlockMethod === 'biometrics' && (
            <>
              <View style={[flexbox.alignCenter, flexbox.justifyCenter, spacings.mbLg]}>
                <Pressable
                  style={{
                    width: 80,
                    height: 80,
                    ...flexbox.center,
                    borderRadius: 50,
                    backgroundColor: theme.secondaryBackground
                  }}
                  onPress={() => {
                    handleBiometricsPrompt().catch(() => {})
                  }}
                >
                  <BiometricsIcon width={72} height={72} color={theme.iconPrimary} />
                </Pressable>
              </View>
              <Button
                text={t('Unlock with password')}
                type="secondary"
                hasBottomSpacing={false}
                onPress={() => setUnlockMethod('password')}
              />
            </>
          )}
          {FEATURES.forgotPasswordResetEnabled && isMobileWebApp && (
            <BottomSheet
              id="forgot-password-modal"
              sheetRef={forgotRef}
              closeBottomSheet={closeForgot}
              type="modal"
              style={{ maxWidth: 512, width: '90%' }}
              backgroundColor={
                themeType === THEME_TYPES.DARK ? 'secondaryBackground' : 'primaryBackground'
              }
            >
              <ForgotPasswordModal onImport={handleConfirmReset} onClose={closeForgot} />
            </BottomSheet>
          )}
        </UnlockPanel>
      </MobileLayoutWrapperMainContent>
    </MobileLayoutContainer>
  )
}

export default React.memo(KeyStoreUnlockScreen)
